import ReactivarClienteDialog from "./components/ReactivarClienteDialog";
// /opt/isp_erp/frontend/src/pages/admin/clientes/UsuariosClientes.jsx
import React, { useEffect, useState, useRef } from "react";
import axios from "../../../api/axios";
import { useAuth } from '../../../auth/useAuth';
import { Link } from "react-router-dom";
import "./UsuariosClientes.css";
import "./UsuariosClientes.pro.css";

export default function UsuariosClientes({ estadoInicial = "todos", titulo = "Clientes" }) {
  const { canExport } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState(estadoInicial);
  // Al pasar de /clientes/activos a /clientes/retirados la pantalla no se vuelve a crear: se actualiza el filtro
  useEffect(() => {
    setFiltroEstado(estadoInicial);
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estadoInicial]);
  const [showEliminarModal, setShowEliminarModal] = useState(false);
  const [clienteToDelete, setClienteToDelete] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [mensajeEliminacion, setMensajeEliminacion] = useState("");

  // Estados para búsqueda y paginación
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [terminoBuscado, setTerminoBuscado] = useState("");
  const [totalClientes, setTotalClientes] = useState(0);
  const [cargandoLista, setCargandoLista] = useState(false);
  const peticionActual = useRef(0);

  // La búsqueda espera 350 ms tras la última tecla para no consultar en cada letra
  useEffect(() => {
    const espera = setTimeout(() => setTerminoBuscado(searchTerm.trim()), 350);
    return () => clearTimeout(espera);
  }, [searchTerm]);

  // Pide al servidor solo la página que se muestra (la búsqueda y el filtro de estado también se hacen allá)
  const consultarClientes = async (pagina, tamano, texto, estado) => {
    const params = { page: pagina, page_size: tamano };
    if (texto) params.q = texto;
    if (estado && estado !== "todos") params.estado = estado;
    const res = await axios.get("/customers/lista", { params });
    return res.data;
  };

  const fetchClientes = async () => {
    const id = ++peticionActual.current;
    setCargandoLista(true);
    try {
      const data = await consultarClientes(currentPage, itemsPerPage, terminoBuscado, filtroEstado);
      if (id !== peticionActual.current) return; // llegó una respuesta más nueva
      if ((data.items || []).length === 0 && data.total > 0 && currentPage > 1) {
        setCurrentPage(Math.max(1, Math.ceil(data.total / itemsPerPage)));
        return;
      }
      setClientes(data.items || []);
      setTotalClientes(data.total || 0);
    } catch (err) {
      if (id !== peticionActual.current) return;
      console.error("Error al cargar clientes:", err);
      alert(err.response?.data?.detail || "Error al cargar clientes");
    } finally {
      if (id === peticionActual.current) {
        setLoading(false);
        setCargandoLista(false);
      }
    }
  };

  useEffect(() => {
    fetchClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, filtroEstado, terminoBuscado]);

  // ===== FUNCIÓN: Exportar clientes a CSV =====
  const exportarClientesCSV = async () => {
    if (!totalClientes) {
      alert("No hay datos de clientes para exportar");
      return;
    }
    setProcesando(true);
    let clientesExportar = [];
    try {
      for (let pagina = 1; ; pagina++) {
        const data = await consultarClientes(pagina, 500, terminoBuscado, filtroEstado);
        clientesExportar = clientesExportar.concat(data.items || []);
        if (clientesExportar.length >= (data.total || 0) || (data.items || []).length === 0) break;
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Error al exportar clientes");
      return;
    } finally {
      setProcesando(false);
    }

    const columnas = [
      "id",
      "tipo_identificacion",
      "identificacion",
      "nombres",
      "apellidos",
      "telefono",
      "email",
      "direccion",
      "ciudad",
      "servicio",
      "plan",
      "estado",
      "ficha",
      "vineta",
      "onu_id",
      "created_at",
      "updated_at"
    ];

    const encabezados = columnas.join(",");
    const filas = clientesExportar.map(cliente => {
      return columnas.map(col => {
        let valor = cliente[col] !== null && cliente[col] !== undefined ? cliente[col] : "";
        if (typeof valor === "string" && (valor.includes(",") || valor.includes("\n") || valor.includes('"'))) {
          valor = valor.replace(/"/g, '""');
          valor = `"${valor}"`;
        }
        return valor;
      }).join(",");
    }).join("\n");

    const contenidoCSV = `\uFEFF${encabezados}\n${filas}`;
    const blob = new Blob([contenidoCSV], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `clientes_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // La lista ya llega filtrada y paginada desde el servidor
  const clientesFiltrados = clientes;
  const totalPages = Math.max(1, Math.ceil(totalClientes / itemsPerPage));
  const currentClientes = clientes;

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const [clienteReactivar, setClienteReactivar] = useState(null);

  // 🟢 FUNCIÓN PARA RETIRAR CLIENTE - VERSIÓN MEJORADA CON MENSAJES CLAROS
  const handleRetirarCliente = async (cliente) => {
    if (!window.confirm(`¿Está seguro de retirar al cliente ${cliente.nombres} ${cliente.apellidos}?`)) {
      return;
    }

    setProcesando(true);
    try {
      const motivoRetiro = window.prompt("Motivo del retiro:", "Retiro voluntario");
      if (motivoRetiro === null) {
        setProcesando(false);
        return;
      }
      const respuesta = await axios.post(`/customers/${cliente.id}/retirar`, {
        motivo: motivoRetiro.trim() || "Retiro voluntario"
      });

      const aviso = respuesta.data?.advertencia;
      alert(aviso
        ? `✅ Cliente retirado.\n\n⚠️ ${aviso}`
        : "✅ Cliente retirado exitosamente. El servicio quedó suspendido.");
      fetchClientes();
    } catch (error) {
      console.error("Error retirando cliente:", error);

      if (error.response?.data) {
        const data = error.response.data;

        // Construir mensaje amigable
        let mensajeUsuario = "❌ No se pudo retirar el cliente\n\n";

        // Si el error tiene la estructura detallada del backend
        if (data.detail && typeof data.detail === 'object') {
          const detalle = data.detail;

          mensajeUsuario += `${detalle.error || 'Error desconocido'}\n`;

          // Mostrar facturas pendientes si existen
          if (detalle.facturas_pendientes && detalle.facturas_pendientes.length > 0) {
            mensajeUsuario += `\n📋 FACTURAS PENDIENTES (${detalle.cantidad}):\n`;
            mensajeUsuario += `💰 Total adeudado: $${detalle.total_pendiente?.toFixed(2)}\n\n`;

            detalle.facturas_pendientes.forEach((factura, index) => {
              const fecha = new Date(factura.fecha_emision).toLocaleDateString();
              mensajeUsuario += `${index + 1}. Factura del ${fecha}\n`;
              mensajeUsuario += `   💵 Monto: $${factura.total?.toFixed(2)}\n`;
              mensajeUsuario += `   📍 Estado: ${factura.estado}\n`;
              mensajeUsuario += `   🆔 ID: ${factura.id}\n\n`;
            });

            mensajeUsuario += `✅ ${detalle.accion_requerida || 'Pague o anule las facturas pendientes'}`;
          }

          // Mostrar promesas activas si existen
          else if (detalle.promesas_activas && detalle.promesas_activas.length > 0) {
            mensajeUsuario += `\n📅 PROMESAS DE PAGO ACTIVAS (${detalle.cantidad}):\n`;
            mensajeUsuario += `💰 Total comprometido: $${detalle.total_promesas?.toFixed(2)}\n\n`;

            detalle.promesas_activas.forEach((promesa, index) => {
              const fecha = new Date(promesa.fecha_compromiso).toLocaleDateString();
              mensajeUsuario += `${index + 1}. Promesa del ${fecha}\n`;
              mensajeUsuario += `   💵 Monto: $${promesa.monto?.toFixed(2)}\n`;
              mensajeUsuario += `   📍 Estado: ${promesa.estado}\n`;
              mensajeUsuario += `   🆔 ID: ${promesa.id}\n\n`;
            });

            mensajeUsuario += `✅ ${detalle.accion_requerida || 'Cumpla o anule las promesas pendientes'}`;
          }
        }
        // Si es un error simple
        else if (data.detail) {
          mensajeUsuario += data.detail;
        }
        // Si es otro tipo de error
        else {
          mensajeUsuario += JSON.stringify(data, null, 2);
        }

        alert(mensajeUsuario);

      } else {
        alert("❌ Error de conexión. Verifique su red e intente nuevamente.");
      }
    } finally {
      setProcesando(false);
    }
  };

  // Función para eliminar cliente (solo retirados)
  const handleEliminarCliente = async () => {
    if (!clienteToDelete) return;

    setProcesando(true);
    setMensajeEliminacion("");
    try {
      const response = await axios.delete(`/customers/${clienteToDelete.id}`);
      setMensajeEliminacion(response.data.instalaciones_eliminadas
        ? `✅ Cliente eliminado. También se eliminaron ${response.data.instalaciones_eliminadas} instalaciones asociadas.`
        : "✅ Cliente eliminado permanentemente");

      setShowEliminarModal(false);
      setClienteToDelete(null);
      fetchClientes();

      setTimeout(() => {
        alert(mensajeEliminacion || "✅ Cliente eliminado permanentemente");
      }, 100);
    } catch (error) {
      console.error("Error eliminando cliente:", error);
      alert(error.response?.data?.detail || "Error al eliminar cliente");
    } finally {
      setProcesando(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando clientes...</p>
      </div>
    );
  }

  return (
    <div className="clientes-container clientes-pro">
      <div className="clientes-header">
        <div>
          <h1>{titulo}</h1>
          <p className="subtitle">Gestión de los clientes del sistema</p>
        </div>
        <div className="clientes-header-acciones">
          {canExport() && (
            <button
              onClick={exportarClientesCSV}
              className="btn-export"
            >
              📥 Exportar CSV
            </button>
          )}
        </div>
      </div>

      {/* Barra de búsqueda y filtros */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Buscar por ID, identificación, nombre, plan, viñeta, ficha..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="clear-search"
            >
              ✕
            </button>
          )}
        </div>

        <div className="filters-group">
          <select
            value={filtroEstado}
            onChange={(e) => {
              setFiltroEstado(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="todos">Todos los estados</option>
            <option value="ACTIVO">✅ Activos</option>
            <option value="SUSPENDIDO">⏸️ Suspendidos</option>
            <option value="RETIRADO">📦 Retirados</option>
            <option value="PENDIENTE_INSTALACION">🛠️ Pendiente de instalación</option>
          </select>

          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="per-page-select"
          >
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
            <option value={250}>250 por página</option>
          </select>

          <span className="results-count">
            {totalClientes.toLocaleString("es-EC")} resultados{cargandoLista ? " · actualizando…" : ""}
          </span>
        </div>
      </div>

      {/* Tabla de clientes */}
      {clientesFiltrados.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>{searchTerm ? "No se encontraron clientes" : "No hay clientes para mostrar"}</h3>
          <p>
            {searchTerm
              ? `No hay resultados para "${searchTerm}"`
              : "Prueba cambiando el filtro de estado"}
          </p>
          {searchTerm && (
            <button onClick={clearSearch} className="btn-link">
              Limpiar búsqueda
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="table-container" style={{ opacity: cargandoLista ? 0.6 : 1, transition: "opacity .15s" }}>
            <table className="clientes-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Contacto</th>
                  <th>Servicio/Plan</th>
                  <th>Viñeta</th>
                  <th>Ficha</th>
                  <th>Estado</th>
                  <th>IP Asignada</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentClientes.map((cliente) => (
                  <tr key={cliente.id}>
                    <td className="id-cell">#{cliente.id}</td>
                    <td>
                      <div className="cliente-info">
                        <div className="cliente-avatar">👤</div>
                        <div>
                          <div className="cliente-nombre">
                            {cliente.nombres} {cliente.apellidos}
                          </div>
                          <div className="cliente-identificacion">
                            {cliente.tipo_identificacion || 'CEDULA'}: {cliente.identificacion}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="contacto-info">
                        {cliente.telefono && (
                          <div className="contacto-item">
                            <span className="contacto-icon">📞</span>
                            {cliente.telefono}
                          </div>
                        )}
                        {cliente.email && (
                          <div className="contacto-item">
                            <span className="contacto-icon">✉️</span>
                            <span className="truncate" title={cliente.email}>{cliente.email}</span>
                          </div>
                        )}
                        {cliente.direccion && (
                          <div className="contacto-item">
                            <span className="contacto-icon">📍</span>
                            <span className="truncate" title={cliente.direccion}>{cliente.direccion}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="servicio-info">
                        <div className="servicio-tipo">
                          {cliente.servicio === 'INTERNET' && '🌐'}
                          {cliente.servicio === 'TV' && '📺'}
                          {cliente.servicio === 'DUOPACK' && '📦'}
                          <span>{cliente.servicio || 'N/A'}</span>
                        </div>
                        <div className="servicio-plan">{cliente.plan || 'Sin plan'}</div>
                      </div>
                    </td>
                    <td>
                      {cliente.vineta ? (
                        <div className="vineta-cell" title={cliente.vineta}>
                          <span className="vineta-icon">📌</span>
                          <span className="truncate">{cliente.vineta}</span>
                        </div>
                      ) : (
                        <span className="empty-value">—</span>
                      )}
                    </td>
                    <td>
                      {cliente.ficha ? (
                        <div className="ficha-cell" title={cliente.ficha}>
                          <span className="ficha-icon">📄</span>
                          <span className="truncate">{cliente.ficha}</span>
                        </div>
                      ) : (
                        <span className="empty-value">—</span>
                      )}
                    </td>
                    <td>
                      <span className={`estado-badge estado-${cliente.estado?.toLowerCase()}`}>
                        {cliente.estado === 'ACTIVO' && '✅ '}
                        {cliente.estado === 'SUSPENDIDO' && '⏸️ '}
                        {cliente.estado === 'RETIRADO' && '📦 '}
                        {cliente.estado === 'PENDIENTE_INSTALACION' && '🛠️ '}
                        {cliente.estado === 'PENDIENTE_INSTALACION' ? 'PENDIENTE DE INSTALACIÓN' : cliente.estado}
                      </span>
                    </td>
                    <td>
                      {cliente.ip_asignada ? (
                        <span className="ip-badge">{cliente.ip_asignada}</span>
                      ) : (
                        <span className="empty-value">—</span>
                      )}
                    </td>
                    <td>
                      <div className="acciones-cell">
                        <Link
                          to={`/clientes/detalle/${cliente.id}`}
                          className="btn-accion ver"
                        >
                          Ver
                        </Link>

                        {cliente.estado === 'ACTIVO' && (
                          <button
                            className="btn-accion retirar"
                            onClick={() => handleRetirarCliente(cliente)}
                            disabled={procesando}
                          >
                            Retirar
                          </button>
                        )}

                        {cliente.estado === 'RETIRADO' && (
                          <button
                            className="btn-accion reactivar"
                            onClick={() => setClienteReactivar(cliente)}
                            disabled={procesando}
                          >
                            Reactivar
                          </button>
                        )}

                        {clienteReactivar && clienteReactivar.id === cliente.id && (
                          <ReactivarClienteDialog
                            cliente={cliente}
                            onCerrar={() => setClienteReactivar(null)}
                            onHecho={() => {
                              setClienteReactivar(null);
                              fetchClientes();
                            }}
                          />
                        )}

                        {cliente.estado === 'RETIRADO' && (
                          <button
                            className="btn-accion eliminar"
                            onClick={() => {
                              setClienteToDelete(cliente);
                              setShowEliminarModal(true);
                            }}
                            disabled={procesando}
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Página {currentPage.toLocaleString("es-EC")} de {totalPages.toLocaleString("es-EC")} · Mostrando {((currentPage - 1) * itemsPerPage + 1).toLocaleString("es-EC")}–{Math.min(currentPage * itemsPerPage, totalClientes).toLocaleString("es-EC")} de {totalClientes.toLocaleString("es-EC")}
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                  title="Primera página"
                >
                  ⏮️
                </button>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                  title="Página anterior"
                >
                  ◀️
                </button>

                <div className="pagination-numbers">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                  title="Página siguiente"
                >
                  ▶️
                </button>
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                  title="Última página"
                >
                  ⏭️
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal para confirmar eliminación */}
      {showEliminarModal && clienteToDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 className="modal-title">⚠️ Eliminar Cliente Permanentemente</h3>
            <p className="modal-text">
              ¿Está seguro que desea eliminar permanentemente al cliente?
            </p>
            <div className="modal-info">
              <p className="font-medium">{clienteToDelete.nombres} {clienteToDelete.apellidos}</p>
              <p className="text-sm">ID: {clienteToDelete.identificacion}</p>
              <p className="text-sm">Plan: {clienteToDelete.plan || 'N/A'}</p>
            </div>
            <p className="modal-warning">
              Esta acción no se puede deshacer.
              Se eliminarán todos los datos del cliente y las instalaciones asociadas.
            </p>
            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowEliminarModal(false);
                  setClienteToDelete(null);
                }}
                className="modal-btn cancel"
                disabled={procesando}
              >
                Cancelar
              </button>
              <button
                onClick={handleEliminarCliente}
                className="modal-btn delete"
                disabled={procesando}
              >
                {procesando ? "Eliminando..." : "Eliminar Permanentemente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
